import { z } from 'zod';
import { AdminClubHostSchema } from './club-hosts.js';
import { AdminClubHostSlotInputSchema } from './admin-hosts.js';
import { ClubStatusSchema } from './clubs.js';
import { NullablePaywallSlugSchema } from '../lib/paywall-slugs.js';

export const AdminClubTelegramSourceStatusSchema = z.enum(['pending', 'active', 'disabled', 'error']);
export type AdminClubTelegramSourceStatus = z.infer<typeof AdminClubTelegramSourceStatusSchema>;

export const AdminClubSaleReadinessStatusSchema = z.enum(['ready', 'not_ready']);
export type AdminClubSaleReadinessStatus = z.infer<typeof AdminClubSaleReadinessStatusSchema>;

export const AdminClubTelegramSourceInputSchema = z.object({
  telegramChatId: z.string().max(64).nullable().optional(),
  telegramTitle: z.string().max(200).nullable().optional(),
  enabled: z.boolean().optional(),
});

export type AdminClubTelegramSourceInput = z.infer<typeof AdminClubTelegramSourceInputSchema>;

export const AdminClubTelegramSourceSchema = z.object({
  id: z.string(),
  type: z.literal('channel'),
  telegramUsername: z.string().nullable(),
  telegramChatId: z.string().nullable(),
  telegramTitle: z.string().nullable(),
  status: AdminClubTelegramSourceStatusSchema,
  enabled: z.boolean(),
  lastEventAt: z.string().nullable(),
  lastError: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export type AdminClubTelegramSource = z.infer<typeof AdminClubTelegramSourceSchema>;

export const AdminReorderClubsSchema = z.object({
  order: z
    .array(z.object({ id: z.string(), index: z.number().int().min(1) }))
    .min(1, 'At least one item required'),
});

export type AdminReorderClubsDto = z.infer<typeof AdminReorderClubsSchema>;

export const AdminCreateClubSchema = z.object({
  title: z.string().min(1, 'Title is required').max(200),
  status: ClubStatusSchema.default('upcoming'),
  paywallSlug: NullablePaywallSlugSchema.optional(),
  bookAuthor: z.string().max(200).optional(),
  primaryHost: AdminClubHostSlotInputSchema.optional(),
  secondaryHost: AdminClubHostSlotInputSchema.nullable().optional(),
  hostFirstName: z.string().max(200).optional(),
  hostLastName: z.string().max(200).optional(),
  hostSubtitle: z.string().max(200).optional(),
  hostAvatarAssetId: z.string().uuid().optional(),
  secondHostFirstName: z.string().max(200).optional(),
  secondHostLastName: z.string().max(200).optional(),
  secondHostSubtitle: z.string().max(200).optional(),
  secondHostAvatarAssetId: z.string().uuid().optional(),
  shortDescription: z.string().max(500).optional(),
  priceAmount: z.number().int().min(0).optional(),
  priceCurrency: z.string().length(3).optional(),
  isPurchasable: z.boolean().optional(),
  coverAssetId: z.string().uuid().optional(),
  telegramSource: AdminClubTelegramSourceInputSchema.nullable().optional(),
});

export type AdminCreateClubDto = z.infer<typeof AdminCreateClubSchema>;

export const AdminUpdateClubSchema = z.object({
  title: z.string().min(1, 'Title is required').max(200).optional(),
  status: ClubStatusSchema.optional(),
  paywallSlug: NullablePaywallSlugSchema.optional(),
  bookAuthor: z.string().max(200).nullable().optional(),
  primaryHost: AdminClubHostSlotInputSchema.nullable().optional(),
  secondaryHost: AdminClubHostSlotInputSchema.nullable().optional(),
  hostFirstName: z.string().max(200).nullable().optional(),
  hostLastName: z.string().max(200).nullable().optional(),
  hostSubtitle: z.string().max(200).nullable().optional(),
  hostAvatarAssetId: z.string().uuid().nullable().optional(),
  secondHostFirstName: z.string().max(200).nullable().optional(),
  secondHostLastName: z.string().max(200).nullable().optional(),
  secondHostSubtitle: z.string().max(200).nullable().optional(),
  secondHostAvatarAssetId: z.string().uuid().nullable().optional(),
  shortDescription: z.string().max(500).nullable().optional(),
  priceAmount: z.number().int().min(0).nullable().optional(),
  priceCurrency: z.string().length(3).nullable().optional(),
  isPurchasable: z.boolean().optional(),
  coverAssetId: z.string().uuid().nullable().optional(),
  telegramSource: AdminClubTelegramSourceInputSchema.nullable().optional(),
  visibility: z.enum(['draft', 'published', 'hidden']).optional(),
});

export type AdminUpdateClubDto = z.infer<typeof AdminUpdateClubSchema>;

export const AdminClubResponseSchema = z.object({
  id: z.string(),
  title: z.string(),
  orderIndex: z.number(),
  status: ClubStatusSchema,
  paywallSlug: z.string().nullable(),
  bookAuthor: z.string().nullable(),
  primaryHostId: z.string().nullable(),
  secondaryHostId: z.string().nullable(),
  host: AdminClubHostSchema,
  secondHost: AdminClubHostSchema.nullable(),
  author: z.string().nullable(),
  authorSubtitle: z.string().nullable(),
  authorAvatarAssetId: z.string().nullable(),
  authorAvatarUrl: z.string().nullable(),
  secondAuthor: z.string().nullable(),
  secondAuthorSubtitle: z.string().nullable(),
  secondAuthorAvatarAssetId: z.string().nullable(),
  secondAuthorAvatarUrl: z.string().nullable(),
  shortDescription: z.string().nullable(),
  coverAssetId: z.string().nullable(),
  coverUrl: z.string().nullable(),
  priceAmount: z.number().nullable(),
  priceCurrency: z.literal('RUB').nullable(),
  isPurchasable: z.boolean(),
  saleReadinessStatus: AdminClubSaleReadinessStatusSchema,
  visibility: z.enum(['draft', 'published', 'hidden']),
  publishedAt: z.string().nullable(),
  telegramSource: AdminClubTelegramSourceSchema.nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
  _count: z.object({ chapters: z.number() }).optional(),
});

export type AdminClubResponse = z.infer<typeof AdminClubResponseSchema>;

export const AdminTrashedClubResponseSchema = AdminClubResponseSchema.extend({
  deletedAt: z.string(),
  purgeAfter: z.string(),
});

export type AdminTrashedClubResponse = z.infer<typeof AdminTrashedClubResponseSchema>;
